import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { ThemeProvider as FluentThemeProvider } from '@fluentui/react';
import { Theme } from './Theme';

interface DarkModeContextValue {
    isDark: boolean;
    toggle: () => void;
}

const DarkModeContext = createContext<DarkModeContextValue>({ isDark: false, toggle: () => {} });

export function useDarkMode() {
    return useContext(DarkModeContext);
}

function getInitialDark(): boolean {
    const stored = localStorage.getItem('agentbox:dark-mode');
    if (stored !== null) return stored === 'true';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

interface Props {
    children: React.ReactNode;
}

export default function TalxisThemeProvider({ children }: Props) {
    const [isDark, setIsDark] = useState(getInitialDark);

    useEffect(() => {
        const theme = Theme.get();
        theme.setDark(isDark);
    }, [isDark]);

    // Listen for OS theme changes when no explicit preference is stored
    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => {
            if (localStorage.getItem('agentbox:dark-mode') === null) {
                setIsDark(e.matches);
            }
        };
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const toggle = useCallback(() => {
        setIsDark((prev) => {
            const next = !prev;
            localStorage.setItem('agentbox:dark-mode', String(next));
            return next;
        });
    }, []);

    const theme = Theme.get();

    return (
        <DarkModeContext.Provider value={{ isDark, toggle }}>
            <FluentThemeProvider theme={theme.main} applyTo="none">
                {children}
            </FluentThemeProvider>
        </DarkModeContext.Provider>
    );
}
